import React from 'react';
import { FaTools, FaOilCan, FaCarBattery, FaCar, FaSnowflake, FaWrench, FaCheckCircle, FaClock, FaShieldAlt, FaUsers, FaAward, FaPhone, FaEnvelope, FaMapMarkerAlt, FaStar, FaCertificate, FaDollarSign } from 'react-icons/fa';

export default function Workshop() {
  const services = [
    {
      icon: FaOilCan,
      title: "Oil Change",
      description: "Regularly changing your oil and filter will help your engine work at its best and extend its lifespan.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: FaTools,
      title: "Brake Repair",
      description: "Brake maintenance is crucial to keeping your vehicle operating safely. Our experts ensure your brakes are in perfect condition.",
      color: "from-red-500 to-red-700"
    },
    {
      icon: FaCar,
      title: "Tires & Wheels",
      description: "Regularly inspect and service your tires to prevent blowouts, flats, or costly accidents. We offer premium tire services.",
      color: "from-green-500 to-green-700"
    },
    {
      icon: FaSnowflake,
      title: "A/C Repair",
      description: "Keep your car cool and increase your gas mileage with regular AC checks and maintenance services.",
      color: "from-cyan-500 to-cyan-700"
    },
    {
      icon: FaCarBattery,
      title: "Battery Service",
      description: "Ensure your vehicle starts reliably. We test, replace, and maintain all types of automotive batteries.",
      color: "from-yellow-500 to-yellow-700"
    },
    {
      icon: FaWrench,
      title: "General Maintenance",
      description: "Comprehensive vehicle maintenance to keep your car running smoothly and efficiently all year round.",
      color: "from-purple-500 to-purple-700"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-20 px-4 md:px-8 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-200/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-full border border-red-100">
            <span className="w-2 h-2 bg-[#DC2626] rounded-full animate-pulse"></span>
            <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">
              GIGA Mobility Workshop
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-8 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
              Professional Auto
            </span>
            <br />
            <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
              Repair Services
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium mb-8">
            Expert automotive repair and maintenance services to keep your vehicle running at its best. Trust our certified technicians for quality workmanship.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-200">
              <FaShieldAlt className="text-[#DC2626] text-lg" />
              <span className="text-sm font-semibold text-gray-700">Certified Technicians</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-200">
              <FaAward className="text-blue-600 text-lg" />
              <span className="text-sm font-semibold text-gray-700">Quality Guaranteed</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-200">
              <FaClock className="text-green-600 text-lg" />
              <span className="text-sm font-semibold text-gray-700">Fast Service</span>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-full border border-red-100">
              <span className="w-2 h-2 bg-[#DC2626] rounded-full animate-pulse"></span>
              <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">
                Our Services
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Auto Repair
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
                Services
              </span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-medium">
              Comprehensive automotive services to keep your vehicle in optimal condition
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-3xl shadow-xl p-8 md:p-10 border-2 border-gray-100 hover:border-[#DC2626]/50 transition-all duration-300 hover:shadow-2xl group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-50/50 to-transparent rounded-full blur-2xl -mr-16 -mt-16 group-hover:from-red-50/50 transition-colors"></div>
                  <div className="relative z-10">
                    <div className={`w-24 h-24 bg-gradient-to-br ${service.color} rounded-3xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <IconComponent className="text-white text-4xl" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-gray-700 leading-relaxed mb-6 text-lg font-medium">{service.description}</p>
                    <a
                      href="#"
                      className="inline-flex items-center gap-2 text-[#DC2626] font-bold hover:gap-3 transition-all text-lg group/link"
                    >
                      Learn More
                      <span className="group-hover/link:translate-x-1 transition-transform">→</span>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-gradient-to-br from-white via-blue-50/40 to-white rounded-3xl shadow-2xl p-10 md:p-16 border-2 border-gray-100 mb-20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -mr-48 -mt-48 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-100/30 rounded-full blur-3xl -ml-48 -mb-48 animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-100/20 rounded-full blur-3xl"></div>

          <div className="relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-full border border-red-100">
                <span className="w-2 h-2 bg-[#DC2626] rounded-full animate-pulse"></span>
                <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">
                  Why Choose Us
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
                <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                  Expert Service
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
                  You Can Trust
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center group bg-white/80 backdrop-blur-sm rounded-2xl p-8 hover:bg-white transition-all duration-300 hover:shadow-xl border-2 border-gray-100 hover:border-[#DC2626]/30">
                <div className="w-24 h-24 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <FaUsers className="text-white text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Technicians</h3>
                <p className="text-gray-700 leading-relaxed font-medium">Certified professionals with years of experience and continuous training</p>
              </div>

              <div className="text-center group bg-white/80 backdrop-blur-sm rounded-2xl p-8 hover:bg-white transition-all duration-300 hover:shadow-xl border-2 border-gray-100 hover:border-blue-500/30">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <FaShieldAlt className="text-white text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Guarantee</h3>
                <p className="text-gray-700 leading-relaxed font-medium">All work backed by our comprehensive quality guarantee and warranty</p>
              </div>

              <div className="text-center group bg-white/80 backdrop-blur-sm rounded-2xl p-8 hover:bg-white transition-all duration-300 hover:shadow-xl border-2 border-gray-100 hover:border-green-500/30">
                <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-700 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <FaClock className="text-white text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast Service</h3>
                <p className="text-gray-700 leading-relaxed font-medium">Quick turnaround times without compromising on quality or safety</p>
              </div>

              <div className="text-center group bg-white/80 backdrop-blur-sm rounded-2xl p-8 hover:bg-white transition-all duration-300 hover:shadow-xl border-2 border-gray-100 hover:border-purple-500/30">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-700 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <FaDollarSign className="text-white text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Affordable Pricing</h3>
                <p className="text-gray-700 leading-relaxed font-medium">Competitive prices with transparent, upfront pricing - no hidden fees</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-br from-white via-blue-50/40 to-white rounded-3xl shadow-2xl p-10 md:p-16 border-2 border-gray-100 mb-20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -mr-48 -mt-48 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-100/30 rounded-full blur-3xl -ml-48 -mb-48 animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#DC2626] uppercase tracking-wide bg-red-50 px-4 py-2 rounded-full">
                  Our Track Record
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Trusted by Thousands
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: FaUsers, value: "50k+", label: "Happy Customers", color: "from-[#DC2626] to-[#B91C1C]" },
                { icon: FaStar, value: "4.9", label: "Average Rating", color: "from-blue-600 to-blue-800" },
                { icon: FaCertificate, value: "15+", label: "Years Experience", color: "from-green-600 to-green-800" },
                { icon: FaCar, value: "100k+", label: "Vehicles Serviced", color: "from-purple-600 to-purple-800" }
              ].map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div key={index} className="text-center group bg-white/60 backdrop-blur-sm rounded-2xl p-6 hover:bg-white transition-all duration-300 hover:shadow-lg border border-gray-100">
                    <div className={`w-20 h-20 bg-gradient-to-br ${stat.color} rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <IconComponent className="text-white text-3xl" />
                    </div>
                    <p className={`text-5xl md:text-6xl font-extrabold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-3`}>{stat.value}</p>
                    <p className="text-gray-700 font-bold text-lg">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Service Process Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-sm font-semibold text-[#DC2626] uppercase tracking-wide bg-red-50 px-4 py-2 rounded-full">
                How It Works
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Our Service
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
                Process
              </span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-medium">
              Simple steps to get your vehicle serviced with professional care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Book Appointment", desc: "Schedule your service online or call us", icon: FaPhone },
              { step: "2", title: "Vehicle Inspection", desc: "Our technicians thoroughly inspect your vehicle", icon: FaCheckCircle },
              { step: "3", title: "Service & Repair", desc: "We perform quality repairs and maintenance", icon: FaWrench },
              { step: "4", title: "Quality Check", desc: "Final inspection to ensure everything is perfect", icon: FaShieldAlt }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="text-center group bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-100 hover:border-[#DC2626]/50 transition-all duration-300 hover:shadow-2xl">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-3xl flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 transition-all duration-300 relative">
                      <span className="text-white font-bold text-3xl absolute">{item.step}</span>
                      <IconComponent className="text-white text-2xl opacity-50" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed font-medium">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-100 hover:border-[#DC2626]/50 transition-all duration-300 hover:shadow-2xl group text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-all duration-300">
              <FaPhone className="text-white text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Call Us</h3>
            <p className="text-gray-600 mb-2">Available 24/7</p>
            <a href="tel:+2348139851110" className="text-[#DC2626] font-bold text-lg hover:underline">
              +234 813 985 1110
            </a>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-100 hover:border-[#DC2626]/50 transition-all duration-300 hover:shadow-2xl group text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-all duration-300">
              <FaEnvelope className="text-white text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Email Us</h3>
            <p className="text-gray-600 mb-2">Get in touch</p>
            <a href="mailto:workshop@gigaautos.com" className="text-[#DC2626] font-bold text-lg hover:underline">
              workshop@gigaautos.com
            </a>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-100 hover:border-[#DC2626]/50 transition-all duration-300 hover:shadow-2xl group text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-all duration-300">
              <FaMapMarkerAlt className="text-white text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Visit Us</h3>
            <p className="text-gray-600 mb-2">Find our location</p>
            <p className="text-[#DC2626] font-bold text-lg">
              Lagos, Nigeria
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-[#DC2626] via-[#C91C1C] to-[#B91C1C] rounded-3xl shadow-2xl p-12 md:p-20 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -ml-48 -mb-48"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          
          <div className="relative z-10 text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
              Ready to Service Your Vehicle?
            </h2>
            <p className="text-xl md:text-2xl text-white/95 mb-10 max-w-3xl mx-auto font-medium leading-relaxed">
              Book an appointment today and experience professional automotive service you can trust. Our certified technicians are ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="/blog/66c6f093dc2c7f86bdc18469"
                className="px-10 py-5 bg-white text-[#DC2626] font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                Contact Us
              </a>
              <a
                href="#"
                className="px-10 py-5 bg-white/10 backdrop-blur-md text-white font-bold text-lg rounded-xl hover:bg-white/20 transition-all duration-300 border-2 border-white/30 transform hover:scale-105 shadow-xl"
              >
                Book Appointment
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
